import React, { useEffect } from "react";
import { connect, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { Spin, Skeleton, Card } from "antd";
import { FormattedMessage } from "react-intl";
import { ProfileOutlined, CodeOutlined, LineChartOutlined } from "@ant-design/icons";
import queryString from "query-string";

import store from "../../../store";

import { selectors as MQTT_SELECTORS } from "../../../ducks/mqtt";
import { actions as NODE_ACTIONS, selectors as NODE_SELECTORS } from "../../../ducks/node";

import NodeChannelFormV4 from "../../../components/NodeChannelFormV4";
import Logs from "../../../components/Logs";
import NodeStatisticsContainer from "../NodeStatistics";
import GlobalErrorBoundaries from "../../../components/ErrorBoundaries/GlobalErrorBoundaries";

import { MQTT_STATUS, NODE_CHANEL_DETAIL_VIEW_TABS } from "../../../lib/utils/constants";
import queryURL from "../../../lib/utils/queryURL";
import useNodeChannels from "../../../hooks/useNodeChannels";
import useNodeCapabilities from "../../../hooks/useNodeCapabilities";
import useNodeStunAddress from "../../../hooks/useNodeStunAddress";

const NodeChannelDetailViewContainer = ({ match, nodes, MQTTConnection, MQTTStatus }) => {
  const {
    location: { search },
  } = window;
  const parsedQueryString = queryString.parse(search);
  const { id, cwid } = match.params;
  const nodeChannels = useNodeChannels();
  const channelConfig = nodeChannels.getIn([`${id}`, "config"]);
  const isStundAddress = useSelector(NODE_SELECTORS.getIsStundAddress);
  const nodeData = nodes.find((node) => node.cwid === cwid);
  useNodeCapabilities();
  useNodeStunAddress();

  const isConnection = MQTTConnection && MQTTStatus === MQTT_STATUS.CONNECTED;

  useEffect(() => {
    if (!channelConfig && isConnection) {
      store.dispatch(NODE_ACTIONS.SET_NODE_CONNECTION(nodeData));
    }
  }, [cwid, channelConfig, nodeData, isConnection]);

  if (!channelConfig || !isStundAddress) {
    return (
      <Spin
        tip={
          <FormattedMessage
            id="NodeChannelDetailViewContainer.loadingChannelData"
            defaultMessage="Loading channel data..."
          />
        }
      >
        <Skeleton active />
      </Spin>
    );
  }

  const openTabs = (key) => {
    queryURL({ parameter: "tab", value: key });
  };

  const tabList = [
    {
      key: NODE_CHANEL_DETAIL_VIEW_TABS.configurations,
      tab: (
        <span>
          <ProfileOutlined />
          <FormattedMessage id="NodeChannelDetailViewContainer.configuration" defaultMessage="Configuration" />
        </span>
      ),
    },
    {
      key: NODE_CHANEL_DETAIL_VIEW_TABS.logs,
      tab: (
        <span>
          <CodeOutlined />
          <FormattedMessage id="NodeChannelDetailViewContainer.logs" defaultMessage="Logs" />
        </span>
      ),
    },
    {
      key: NODE_CHANEL_DETAIL_VIEW_TABS.statistics,
      tab: (
        <span>
          <LineChartOutlined />
          <FormattedMessage id="NodeChannelDetailViewContainer.statistics" defaultMessage="Statistics" />
        </span>
      ),
    },
  ];

  return (
    <GlobalErrorBoundaries>
      <Card
        tabProps={{ size: "default" }}
        activeTabKey={parsedQueryString?.tab || NODE_CHANEL_DETAIL_VIEW_TABS.configurations}
        tabList={tabList}
        onTabChange={openTabs}
      >
        {(parsedQueryString?.tab === NODE_CHANEL_DETAIL_VIEW_TABS.configurations || !parsedQueryString?.tab) && (
          <NodeChannelFormV4 channelConfig={channelConfig} id={id} detailView editMode />
        )}
        {parsedQueryString?.tab === NODE_CHANEL_DETAIL_VIEW_TABS.logs && <Logs channelId={id} channelDetailView />}
        {parsedQueryString?.tab === NODE_CHANEL_DETAIL_VIEW_TABS.statistics && (
          <NodeStatisticsContainer channelId={id} channelDetailView />
        )}
      </Card>
    </GlobalErrorBoundaries>
  );
};

NodeChannelDetailViewContainer.propTypes = {
  nodes: PropTypes.arrayOf(
    PropTypes.shape({
      cnn: PropTypes.string.isRequired,
      cwid: PropTypes.string.isRequired,
      online: PropTypes.string,
    })
  ).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired,
      cwid: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  MQTTConnection: PropTypes.bool,
  MQTTStatus: PropTypes.string,
};

NodeChannelDetailViewContainer.defaultProps = {
  MQTTConnection: null,
  MQTTStatus: null,
};

const mapStateToProps = (state) => ({
  nodes: MQTT_SELECTORS.getNodes(state),
  MQTTConnection: MQTT_SELECTORS.getMqttConnection(state),
  MQTTStatus: MQTT_SELECTORS.getStatus(state),
});

export default connect(mapStateToProps, null)(NodeChannelDetailViewContainer);
